import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTour } from '../hooks/useTour';
import { methodsTourSteps } from '../tours/methodsTour';
import {
  Search, Clock, BarChart3, X, Users, BookOpen,
  Lightbulb, CheckSquare, Package, MessageSquare,
  GraduationCap, Printer, ShoppingCart, PlusCircle,
  Heart, Filter, TrendingUp, Zap, UserCheck, UsersRound,
  ChevronRight, ChevronDown, ChevronUp,
  Lock, Unlock, CreditCard, CheckCircle,
} from 'lucide-react';
import { methodsApi, customMethodsApi, methodPurchaseApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import { SkeletonCard } from '../components/Skeleton';
import './Methods.css';

/* ── Interfaces ── */
interface Category {
  id: string;
  name: string;
  description: string;
  icon: string;
  method_count?: number;
}

interface MethodStep { step: number; text: string; }

interface Method {
  id: string;
  name: string;
  description: string | null;
  benefit: string | null;
  instructions: MethodStep[] | null;
  debriefing: string | null;
  materials: string | null;
  theory_background: string | null;
  category_id: string | null;
  duration_minutes: number | null;
  duration_max_minutes: number | null;
  min_participants: number | null;
  max_participants: number | null;
  difficulty_level: string;
  applicable_to: string | null;
  usage_count: number;
  rating_avg: number | null;
  is_custom: boolean;
  is_purchasable?: boolean;
  is_starter?: boolean;
  price_eur?: number | null;
  _isCustomTemplate?: boolean;
}

interface CustomSection { name: string; content: string; }

interface CustomMethod {
  id: string;
  name: string;
  description: string | null;
  benefit: string | null;
  instructions: MethodStep[] | null;
  debriefing: string | null;
  materials: string | null;
  theory_background: string | null;
  category_id: string | null;
  duration_minutes: number | null;
  duration_max_minutes: number | null;
  min_participants: number | null;
  max_participants: number | null;
  difficulty_level: string;
  custom_sections: CustomSection[] | null;
  usage_count: number;
  is_active: boolean;
}

interface MethodStats {
  total_methods: number;
  custom_methods: number;
  total_usage: number;
  categories: number;
}

/* ── Helpers ── */
const difficultyLabels: Record<string, string> = {
  beginner: 'Einsteiger',
  intermediate: 'Fortgeschritten',
  advanced: 'Experte',
};

const difficultyColors: Record<string, string> = {
  beginner: 'var(--success)',
  intermediate: 'var(--accent)',
  advanced: 'var(--danger)',
};

const catIcons: Record<string, string> = {
  Coaching: '\u{1F4AC}',
  Team: '\u{1F465}',
  Konflikt: '\u{26A1}',
  'F\u00FChrung': '\u{1F3AF}',
  Karriere: '\u{1F680}',
  'K\u00F6rper & Gesundheit': '\u{1F9D8}',
  Kreativ: '\u{1F3A8}',
};

function formatDuration(min: number | null, max: number | null): string {
  if (!min) return '';
  if (max && max !== min) return `${min}\u2013${max} Min.`;
  return `${min} Min.`;
}

function formatParticipants(min: number | null, max: number | null): string {
  if (!min && !max) return '';
  if (!max) return `ab ${min} Pers.`;
  if (min === max) return `${min} Pers.`;
  return `${min ?? 1}\u2013${max} Pers.`;
}

/* ── Quick-Start Karten Konfig ── */
const quickCards = [
  {
    title: 'Schnell starten',
    desc: 'Die beliebtesten Methoden f\u00FCr den sofortigen Einsatz',
    icon: <Zap size={28} />,
    badge: 'Top 12',
    className: 'md-quick-card-1',
  },
  {
    title: 'F\u00FCr 1:1 Coaching',
    desc: 'Bew\u00E4hrte Methoden f\u00FCr Einzelsitzungen',
    icon: <UserCheck size={28} />,
    badge: '1 : 1',
    className: 'md-quick-card-2',
  },
  {
    title: 'F\u00FCr Teams & Gruppen',
    desc: 'Workshop-Methoden f\u00FCr Gruppenarbeit',
    icon: <UsersRound size={28} />,
    badge: 'Teams',
    className: 'md-quick-card-3',
  },
];

/* ═══════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════ */
export default function Methods() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  // State
  const [categories, setCategories] = useState<Category[]>([]);
  const [methods, setMethods] = useState<Method[]>([]);
  const [customMethods, setCustomMethods] = useState<CustomMethod[]>([]);
  const [recentMethods, setRecentMethods] = useState<Method[]>([]);
  const [stats, setStats] = useState<MethodStats | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState<string>('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('recommended');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('');
  const [showAll, setShowAll] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<Method | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Purchase/Credit State
  const [unlockedIds, setUnlockedIds] = useState<Set<string>>(new Set());
  const [availableCredits, setAvailableCredits] = useState(0);
  const [hasAllAccess, setHasAllAccess] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  // Load data
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [libRes, customRes] = await Promise.all([
        methodsApi.list({
          category_id: selectedCat || undefined,
          search: search || undefined,
          difficulty: difficultyFilter || undefined,
          sort: sort || undefined,
        }),
        customMethodsApi.list(),
      ]);
      setMethods(libRes.data.items);
      setTotal(libRes.data.total);
      let filtered = customRes.data.items as CustomMethod[];
      if (selectedCat) filtered = filtered.filter((m) => m.category_id === selectedCat);
      if (search) {
        const q = search.toLowerCase();
        filtered = filtered.filter(
          (m) => m.name.toLowerCase().includes(q) || (m.description && m.description.toLowerCase().includes(q))
        );
      }
      setCustomMethods(filtered);
      setShowAll(false);
    } catch { /* ignore */ }
    setLoading(false);
  }, [selectedCat, search, sort, difficultyFilter]);

  const loadCreditsAndUnlocks = useCallback(() => {
    methodPurchaseApi.getUnlocked()
      .then((res) => setUnlockedIds(new Set(res.data.unlocked_method_ids)))
      .catch(() => {});
    methodPurchaseApi.getCredits()
      .then((res) => {
        setAvailableCredits(res.data.available_credits);
        setHasAllAccess(res.data.has_all_access);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Initial loads
    methodsApi.categories().then((res) => setCategories(res.data));
    methodsApi.recent(5).then((res) => setRecentMethods(res.data.items)).catch(() => {});
    methodsApi.stats().then((res) => setStats(res.data)).catch(() => {});
    loadCreditsAndUnlocks();
  }, []); // eslint-disable-line

  useEffect(() => {
    const timer = setTimeout(load, 200);
    return () => clearTimeout(timer);
  }, [load]);

  const getCatName = (id: string | null) => categories.find((c) => c.id === id)?.name || '';
  const getCatIcon = (name: string) => catIcons[name] || '\u{1F4CB}';

  const openDetail = async (m: Method) => {
    setSelectedMethod(m);
    setDetailLoading(true);
    try {
      if (m._isCustomTemplate) {
        const res = await customMethodsApi.get(m.id);
        setSelectedMethod({ ...res.data, is_custom: true, _isCustomTemplate: true });
      } else {
        const res = await methodsApi.get(m.id);
        setSelectedMethod(res.data);
      }
    } catch { /* Fallback: Listenversion */ }
    finally { setDetailLoading(false); }
  };

  const handleUnlock = async (methodId: string) => {
    setUnlocking(true);
    try {
      const res = await methodPurchaseApi.unlock(methodId);
      if (res.data.unlocked) {
        showToast('Methode freigeschaltet!', 'success');
        setUnlockedIds((prev) => {
          const next = new Set(Array.from(prev));
          next.add(methodId);
          return next;
        });
        setAvailableCredits(res.data.remaining_credits);
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Freischaltung fehlgeschlagen';
      showToast(detail, 'error');
    }
    setUnlocking(false);
  };

  const isMethodLocked = (m: Method): boolean => {
    if (m.is_starter) return false;
    if (!m.is_purchasable) return false;
    if (hasAllAccess) return false;
    return !unlockedIds.has(m.id);
  };

  const allCount = total + customMethods.length;
  useTour('methods', methodsTourSteps, { delay: 800 });

  return (
    <div className="md-dashboard">

      {/* ══ HEADER ══ */}
      <div className="md-dash-header">
        <h1>Methoden-Bibliothek</h1>
        <span className="md-dash-sub">
          {allCount} Methoden &middot; {categories.length} Kategorien
          {stats && stats.custom_methods > 0 && ` \u00B7 ${stats.custom_methods} eigene`}
        </span>
      </div>

      {/* ══ TOOLBAR ══ */}
      <div className="md-toolbar">
        <div className="md-search-box" data-tour="methods-search">
          <Search size={16} className="md-search-icon" />
          <input
            type="text"
            placeholder="Methoden suchen \u2026"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="md-toolbar-select"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
        >
          <option value="recommended">Sortieren: Empfohlen</option>
          <option value="name_asc">Sortieren: A &rarr; Z</option>
          <option value="popular">Sortieren: Beliebtheit</option>
          <option value="newest">Sortieren: Neueste</option>
        </select>
        <button
          className="md-btn md-btn-outline"
          onClick={() => navigate('/methoden/erstellen/wizard')}
          data-tour="methods-create"
        >
          <PlusCircle size={16} />
          Eigene Methode
        </button>
        <button
          className="md-btn md-btn-primary"
          onClick={() => navigate('/methoden/shop')}
          data-tour="methods-shop"
          style={{ position: 'relative' }}
        >
          <ShoppingCart size={16} />
          Shop
          {availableCredits > 0 && (
            <span style={{
              position: 'absolute',
              top: -6,
              right: -6,
              background: 'var(--success)',
              color: '#fff',
              fontSize: '0.65rem',
              fontWeight: 700,
              width: 20,
              height: 20,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid var(--bg-card)',
            }}>
              {availableCredits}
            </span>
          )}
        </button>
      </div>

      {/* ══ QUICK-START ══ */}
      <div className="md-quick-start">
        {quickCards.map((qc, i) => (
          <div key={i} className={`md-quick-card ${qc.className}`}>
            <span className="md-quick-badge">{qc.badge}</span>
            <div className="md-quick-icon">{qc.icon}</div>
            <h3>{qc.title}</h3>
            <p>{qc.desc}</p>
          </div>
        ))}
      </div>

      {/* ══ MAIN GRID ══ */}
      <div className="md-main-grid" data-tour="methods-grid">

        {/* ── CENTER ── */}
        <div className="md-center">

          {/* Zuletzt verwendet */}
          {recentMethods.length > 0 && (
            <div className="md-section-box">
              <div className="md-section-header">
                <span className="md-section-title"><Clock size={16} /> Zuletzt verwendet</span>
                <span className="md-section-link" onClick={() => {}}>Alle anzeigen <ChevronRight size={14} /></span>
              </div>
              <div className="md-recent-scroll">
                {recentMethods.map((m) => (
                  <div key={m.id} className="md-recent-card" onClick={() => openDetail(m)}>
                    <div className="md-recent-cat">{getCatName(m.category_id)}</div>
                    <div className="md-recent-name">{m.name}</div>
                    <div className="md-recent-meta">
                      {m.duration_minutes && (
                        <span><Clock size={12} /> {formatDuration(m.duration_minutes, m.duration_max_minutes)}</span>
                      )}
                      <span>
                        <span className="md-diff-dot" style={{ background: difficultyColors[m.difficulty_level] }} />
                        {difficultyLabels[m.difficulty_level]}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Kategorien */}
          <div className="md-section-box">
            <div className="md-section-header">
              <span className="md-section-title"><BookOpen size={16} /> Kategorien</span>
            </div>
            <div className="md-cat-grid">
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  className={`md-cat-card ${selectedCat === cat.id ? 'md-cat-active' : ''}`}
                  onClick={() => setSelectedCat(selectedCat === cat.id ? '' : cat.id)}
                >
                  <span className="md-cat-icon">{getCatIcon(cat.name)}</span>
                  <div className="md-cat-name">{cat.name}</div>
                  <div className="md-cat-count">{cat.method_count ?? 0} Methoden</div>
                </div>
              ))}
              <div
                className={`md-cat-card ${selectedCat === '' ? 'md-cat-active' : ''}`}
                onClick={() => setSelectedCat('')}
              >
                <span className="md-cat-icon">{'\u{1F4CB}'}</span>
                <div className="md-cat-name">Alle</div>
                <div className="md-cat-count">{allCount} gesamt</div>
              </div>
            </div>
          </div>

          {/* Alle Methoden */}
          <div className="md-section-box">
            <div className="md-section-header">
              <span className="md-section-title"><BookOpen size={16} /> Alle Methoden ({allCount})</span>
            </div>

            <div className="md-chip-row">
              <span className={`md-chip ${difficultyFilter === '' ? 'md-chip-active' : ''}`} onClick={() => setDifficultyFilter('')}>Alle</span>
              <span className={`md-chip ${difficultyFilter === 'beginner' ? 'md-chip-active' : ''}`} onClick={() => setDifficultyFilter('beginner')}>Einsteiger</span>
              <span className={`md-chip ${difficultyFilter === 'intermediate' ? 'md-chip-active' : ''}`} onClick={() => setDifficultyFilter('intermediate')}>Fortgeschritten</span>
              <span className={`md-chip ${difficultyFilter === 'advanced' ? 'md-chip-active' : ''}`} onClick={() => setDifficultyFilter('advanced')}>Experte</span>
            </div>

            {(() => {
              // Alle Karten zusammenführen: Custom zuerst, dann Bibliothek
              const allCards: ({ type: 'custom'; data: CustomMethod } | { type: 'lib'; data: Method })[] = [
                ...customMethods.map((cm) => ({ type: 'custom' as const, data: cm })),
                ...methods.map((m) => ({ type: 'lib' as const, data: m })),
              ];
              const visibleCards = showAll ? allCards : allCards.slice(0, 6);
              const hiddenCount = allCards.length - 6;

              if (loading) {
                return (
                  <div className="md-methods-grid">
                    {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
                  </div>
                );
              }

              if (allCards.length === 0) {
                return <div className="md-empty">Keine Methoden gefunden.</div>;
              }

              return (
                <>
                  <div className="md-methods-grid">
                    {visibleCards.map((card) =>
                      card.type === 'custom' ? (
                        <div
                          key={`custom-${card.data.id}`}
                          className="md-method-card md-method-card-custom"
                          onClick={() => openDetail({
                            ...(card.data as CustomMethod), applicable_to: null, rating_avg: null,
                            is_custom: true, _isCustomTemplate: true,
                          })}
                        >
                          <div className="md-card-accent md-card-accent-custom" />
                          <div className="md-card-body">
                            <span className="md-badge-custom">Eigene Methode</span>
                            <div className="md-card-title">{card.data.name}</div>
                            <p className="md-card-desc">{card.data.description}</p>
                          </div>
                          <div className="md-card-footer">
                            {card.data.duration_minutes && (
                              <span className="md-meta-badge"><Clock size={12} /> {formatDuration(card.data.duration_minutes, card.data.duration_max_minutes)}</span>
                            )}
                            {(card.data.min_participants || card.data.max_participants) && (
                              <span className="md-meta-badge"><Users size={12} /> {formatParticipants(card.data.min_participants, card.data.max_participants)}</span>
                            )}
                            <span className="md-meta-badge">
                              <span className="md-diff-dot" style={{ background: difficultyColors[card.data.difficulty_level] }} />
                              {difficultyLabels[card.data.difficulty_level]}
                            </span>
                            <Heart size={14} className="md-card-fav active" />
                          </div>
                        </div>
                      ) : (
                        <div key={card.data.id} className="md-method-card" onClick={() => openDetail(card.data as Method)}
                          style={{ position: 'relative' }}
                        >
                          <div className="md-card-accent" />

                          {/* Lock/Unlock Indicator */}
                          {((card.data as Method).is_purchasable || (card.data as Method).is_starter) && (
                            <div style={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              zIndex: 2,
                            }}>
                              {(card.data as Method).is_starter ? (
                                <span style={{
                                  display: 'flex', alignItems: 'center', gap: 4,
                                  background: 'var(--success)', color: '#fff',
                                  fontSize: '0.65rem', fontWeight: 700,
                                  padding: '2px 8px', borderRadius: 12,
                                }}>
                                  <CheckCircle size={12} /> Inklusive
                                </span>
                              ) : unlockedIds.has(card.data.id) || hasAllAccess ? (
                                <span style={{
                                  display: 'flex', alignItems: 'center', gap: 4,
                                  background: 'var(--success)', color: '#fff',
                                  fontSize: '0.65rem', fontWeight: 700,
                                  padding: '2px 8px', borderRadius: 12,
                                }}>
                                  <CheckCircle size={12} /> Freigeschaltet
                                </span>
                              ) : (
                                <span style={{
                                  display: 'flex', alignItems: 'center', gap: 4,
                                  background: 'var(--bg)', color: 'var(--text-muted)',
                                  fontSize: '0.65rem', fontWeight: 600,
                                  padding: '2px 8px', borderRadius: 12,
                                  border: '1px solid var(--border)',
                                }}>
                                  <Lock size={11} /> Credits nötig
                                </span>
                              )}
                            </div>
                          )}

                          <div className="md-card-body">
                            <span className="md-card-cat">{getCatName((card.data as Method).category_id)}</span>
                            <div className="md-card-title">{card.data.name}</div>
                            <p className="md-card-desc">{(card.data as Method).benefit || card.data.description}</p>
                          </div>
                          <div className="md-card-footer">
                            {card.data.duration_minutes && (
                              <span className="md-meta-badge"><Clock size={12} /> {formatDuration(card.data.duration_minutes, card.data.duration_max_minutes)}</span>
                            )}
                            {(card.data.min_participants || card.data.max_participants) && (
                              <span className="md-meta-badge"><Users size={12} /> {formatParticipants(card.data.min_participants, card.data.max_participants)}</span>
                            )}
                            <span className="md-meta-badge">
                              <span className="md-diff-dot" style={{ background: difficultyColors[card.data.difficulty_level] }} />
                              {difficultyLabels[card.data.difficulty_level]}
                            </span>
                            <Heart size={14} className="md-card-fav" />
                          </div>
                        </div>
                      )
                    )}
                  </div>
                  {hiddenCount > 0 && (
                    <button
                      className="md-show-more-btn"
                      onClick={() => setShowAll(!showAll)}
                    >
                      {showAll ? (
                        <><ChevronUp size={16} /> Weniger anzeigen</>
                      ) : (
                        <><ChevronDown size={16} /> {hiddenCount} weitere Methoden anzeigen</>
                      )}
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* ── SIDEBAR ── */}
        <div className="md-sidebar">

          {/* Filter */}
          <div className="md-sidebar-box">
            <div className="md-sidebar-title"><Filter size={15} /> Filter</div>

            <div className="md-filter-group">
              <span className="md-filter-label">Schwierigkeit</span>
              {['beginner', 'intermediate', 'advanced'].map((d) => (
                <label key={d} className="md-filter-option">
                  <input
                    type="checkbox"
                    checked={difficultyFilter === '' || difficultyFilter === d}
                    onChange={() => setDifficultyFilter(difficultyFilter === d ? '' : d)}
                  />
                  {difficultyLabels[d]}
                </label>
              ))}
            </div>

            <div className="md-filter-group">
              <span className="md-filter-label">Kategorie</span>
              {categories.slice(0, 5).map((cat) => (
                <label key={cat.id} className="md-filter-option">
                  <input
                    type="checkbox"
                    checked={selectedCat === '' || selectedCat === cat.id}
                    onChange={() => setSelectedCat(selectedCat === cat.id ? '' : cat.id)}
                  />
                  {cat.name}
                  <span className="md-filter-count">{cat.method_count ?? 0}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="md-sidebar-box">
              <div className="md-sidebar-title"><TrendingUp size={15} /> Deine Statistik</div>
              <div className="md-stat-row">
                <span>Eingesetzt</span>
                <strong>{stats.total_usage}&times;</strong>
              </div>
              <div className="md-stat-row">
                <span>Eigene Methoden</span>
                <strong>{stats.custom_methods}</strong>
              </div>
              <div className="md-stat-row">
                <span>Kategorien</span>
                <strong>{stats.categories}</strong>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="md-cta-box">
            <p>Erweitere dein Repertoire mit <strong>Methoden-Bundles</strong> im Shop</p>
            <button className="md-btn md-btn-primary md-btn-full" onClick={() => navigate('/methoden/shop')}>
              <ShoppingCart size={16} /> Zum Shop
            </button>
          </div>
        </div>

      </div>

      {/* ══ DETAIL-MODAL ══ */}
      {selectedMethod && (
        <div className="modal-overlay" onClick={() => setSelectedMethod(null)}>
          <div className="md-modal" onClick={(e) => e.stopPropagation()}>
            <div className="md-accent" />
            <div className="md-header">
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 className="md-title">{selectedMethod.name}</h2>
                {selectedMethod._isCustomTemplate ? (
                  <span className="md-badge-custom">Eigene Methode</span>
                ) : (
                  <span className="md-category">{getCatName(selectedMethod.category_id)}</span>
                )}
              </div>
              <div className="md-actions">
                {!isMethodLocked(selectedMethod) && (
                  <button
                    className="md-btn md-btn-outline md-btn-sm"
                    onClick={() => window.open(`/methoden/${selectedMethod.id}/arbeitsblatt`, '_blank')}
                  >
                    <Printer size={14} /> Arbeitsblatt
                  </button>
                )}
                <button className="btn-icon" onClick={() => setSelectedMethod(null)}>
                  <X size={20} />
                </button>
              </div>
            </div>

            {detailLoading ? (
              <div style={{ padding: 64, textAlign: 'center', color: 'var(--text-muted)' }}>Lade Details&hellip;</div>
            ) : (
              <div className="method-detail">
                {/* Meta-Badges */}
                <div className="method-meta">
                  {selectedMethod.duration_minutes && (
                    <span className="meta-item meta-badge"><Clock size={14} /> {formatDuration(selectedMethod.duration_minutes, selectedMethod.duration_max_minutes)}</span>
                  )}
                  {(selectedMethod.min_participants || selectedMethod.max_participants) && (
                    <span className="meta-item meta-badge"><Users size={14} /> {formatParticipants(selectedMethod.min_participants, selectedMethod.max_participants)}</span>
                  )}
                  <span className="meta-item meta-badge" style={{ color: difficultyColors[selectedMethod.difficulty_level] }}>
                    <BarChart3 size={14} /> {difficultyLabels[selectedMethod.difficulty_level]}
                  </span>
                </div>

                {/* Freischalten-Button wenn gesperrt / Starter-Badge / Freigeschaltet */}
                {(selectedMethod.is_purchasable || selectedMethod.is_starter) && !selectedMethod._isCustomTemplate && (
                  <div style={{
                    padding: '12px 16px',
                    borderRadius: 10,
                    background: selectedMethod.is_starter || !isMethodLocked(selectedMethod)
                      ? 'rgba(91,140,90,0.06)'
                      : 'rgba(176,116,104,0.06)',
                    border: `1px solid ${selectedMethod.is_starter || !isMethodLocked(selectedMethod) ? 'var(--success)' : 'var(--border)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    marginBottom: 16,
                  }}>
                    {selectedMethod.is_starter ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CheckCircle size={18} style={{ color: 'var(--success)' }} />
                        <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--success)' }}>
                          Starter-Methode — in deinem Plan enthalten
                        </span>
                      </div>
                    ) : isMethodLocked(selectedMethod) ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Lock size={18} style={{ color: 'var(--text-muted)' }} />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Premium-Methode</div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                              {availableCredits > 0
                                ? `1 Credit verwenden (${availableCredits} verfügbar)`
                                : 'Keine Credits — kaufe ein Bundle im Shop'}
                            </div>
                          </div>
                        </div>
                        {availableCredits > 0 ? (
                          <button
                            className="btn btn-primary btn-sm"
                            disabled={unlocking}
                            onClick={(e) => { e.stopPropagation(); handleUnlock(selectedMethod.id); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
                          >
                            <Unlock size={14} />
                            {unlocking ? 'Wird freigeschaltet...' : 'Freischalten (1 Credit)'}
                          </button>
                        ) : (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={(e) => { e.stopPropagation(); navigate('/methoden/shop'); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
                          >
                            <ShoppingCart size={14} /> Zum Shop
                          </button>
                        )}
                      </>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CheckCircle size={18} style={{ color: 'var(--success)' }} />
                        <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--success)' }}>
                          Freigeschaltet
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {selectedMethod.description && (
                  <div className="method-desc-block"><p>{selectedMethod.description}</p></div>
                )}

                {selectedMethod.benefit && (
                  <div className="method-section">
                    <h4 className="method-section-title">
                      <span className="section-icon-wrap" style={{ background: 'rgba(176,116,104,0.1)' }}>
                        <Lightbulb size={16} style={{ color: 'var(--primary)' }} />
                      </span>
                      Nutzen &amp; Einsatzgebiet
                    </h4>
                    <p>{selectedMethod.benefit}</p>
                  </div>
                )}

                {isMethodLocked(selectedMethod) ? (
                  <div style={{
                    marginTop: 8,
                    padding: '24px 20px',
                    background: 'var(--bg)',
                    borderRadius: 10,
                    border: '1px dashed var(--border)',
                    textAlign: 'center',
                  }}>
                    <Lock size={32} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
                    <h4 style={{ margin: '0 0 6px', fontSize: '1rem' }}>Ablauf, Materialien &amp; mehr</h4>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0 0 16px' }}>
                      Schalte diese Methode frei, um die vollständige Durchführung, Materialien, Nachbesprechung und den Theoriehintergrund zu sehen.
                    </p>
                    {availableCredits > 0 ? (
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={unlocking}
                        onClick={(e) => { e.stopPropagation(); handleUnlock(selectedMethod.id); }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        <Unlock size={14} />
                        {unlocking ? 'Wird freigeschaltet...' : 'Freischalten (1 Credit)'}
                      </button>
                    ) : (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={(e) => { e.stopPropagation(); navigate('/methoden/shop'); }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        <ShoppingCart size={14} /> Credits im Shop kaufen
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {selectedMethod.instructions && selectedMethod.instructions.length > 0 && (
                      <div className="method-section">
                        <h4 className="method-section-title">
                          <span className="section-icon-wrap" style={{ background: 'rgba(176,116,104,0.1)' }}>
                            <CheckSquare size={16} style={{ color: 'var(--primary)' }} />
                          </span>
                          Ablauf
                        </h4>
                        <ol className="method-steps">
                          {selectedMethod.instructions.map((s) => (
                            <li key={s.step} className="method-step">
                              <span className="step-number">{s.step}</span>
                              <span className="step-text">{s.text}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {selectedMethod.materials && (
                      <div className="method-section">
                        <h4 className="method-section-title">
                          <span className="section-icon-wrap" style={{ background: 'rgba(176,116,104,0.08)' }}>
                            <Package size={16} style={{ color: 'var(--primary-light)' }} />
                          </span>
                          Materialien
                        </h4>
                        <p>{selectedMethod.materials}</p>
                      </div>
                    )}

                    {selectedMethod.debriefing && (
                      <div className="method-section">
                        <h4 className="method-section-title">
                          <span className="section-icon-wrap" style={{ background: 'rgba(91,140,90,0.08)' }}>
                            <MessageSquare size={16} style={{ color: 'var(--success)' }} />
                          </span>
                          Nachbesprechung
                        </h4>
                        <p>{selectedMethod.debriefing}</p>
                      </div>
                    )}

                    {selectedMethod.theory_background && (
                      <div className="method-section method-section-theory">
                        <h4 className="method-section-title">
                          <span className="section-icon-wrap" style={{ background: 'var(--bg)' }}>
                            <GraduationCap size={16} style={{ color: 'var(--text-muted)' }} />
                          </span>
                          Theoriehintergrund
                        </h4>
                        <p>{selectedMethod.theory_background}</p>
                      </div>
                    )}

                    {(selectedMethod as any).custom_sections &&
                      ((selectedMethod as any).custom_sections as CustomSection[]).map((sec, i) => (
                        <div key={i} className="method-section">
                          <h4 className="method-section-title">
                            <span className="section-icon-wrap" style={{ background: 'rgba(176,116,104,0.1)' }}>
                              <BookOpen size={16} style={{ color: 'var(--primary)' }} />
                            </span>
                            {sec.name}
                          </h4>
                          <p>{sec.content}</p>
                        </div>
                      ))
                    }
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
